import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { SchoolRolesSection } from "pages/app/workspace/sections/school-roles-section";
import { SchoolSportsSection } from "pages/app/workspace/sections/school-sports-section";
import {
  SchoolDocumentsPanel,
  SchoolGalleryPanel,
  SchoolsSection,
} from "pages/app/workspace/sections/schools-section";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { getVisiblePortalSections } from "utils/portal-access";
import {
  SchoolHubContentStack,
  SchoolHubTabButton,
  SchoolHubTabRow,
} from "pages/app/workspace/styles/school-hub.page.inline.styles";

export type SchoolHubTab = "overview" | "staff" | "programmes" | "media" | "documents";

const SCHOOL_HUB_TABS: Array<{ id: SchoolHubTab; label: string; sectionKey: string }> = [
  { id: "overview", label: "Overview", sectionKey: "schools" },
  { id: "staff", label: "Staff", sectionKey: "school_roles" },
  { id: "programmes", label: "Programmes", sectionKey: "school_sports" },
  { id: "media", label: "Media", sectionKey: "schools" },
  { id: "documents", label: "Documents", sectionKey: "schools" },
];

const parseSchoolHubTab = (search: string, visibleSections: string[]): SchoolHubTab => {
  const tab = new URLSearchParams(search).get("tab") as SchoolHubTab | null;
  const allowedTabs = SCHOOL_HUB_TABS.filter((entry) =>
    visibleSections.includes(entry.sectionKey),
  ).map((entry) => entry.id);

  if (tab && allowedTabs.includes(tab)) {
    return tab;
  }

  return allowedTabs[0] || "overview";
};

const SchoolHubPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { portalAccess } = useAuth();
  const { data: currentUser, isLoading: isProfileLoading } = useQuery(buildCurrentUserQueryOptions());
  const effectivePortalAccess = currentUser?.portal_access || portalAccess;
  const visibleSections = getVisiblePortalSections(effectivePortalAccess);
  const activeTab = useMemo(
    () => parseSchoolHubTab(location.search, visibleSections),
    [location.search, visibleSections],
  );
  const { schoolId } = useScopedSchoolSelection({
    portalAccess: effectivePortalAccess,
  });

  const availableTabs = useMemo(
    () => SCHOOL_HUB_TABS.filter((entry) => visibleSections.includes(entry.sectionKey)),
    [visibleSections],
  );

  const setTab = useCallback(
    (tab: SchoolHubTab) => {
      history.replace({
        pathname: location.pathname,
        search: tab === "overview" ? "" : `?tab=${tab}`,
      });
    },
    [history, location.pathname],
  );

  const headerTabs = useMemo(
    () =>
      availableTabs.length > 1 ? (
        <SchoolHubTabRow>
          {availableTabs.map((tab) => (
            <SchoolHubTabButton
              $active={activeTab === tab.id}
              key={tab.id}
              onClick={() => setTab(tab.id)}
              type="button"
            >
              {tab.label}
            </SchoolHubTabButton>
          ))}
        </SchoolHubTabRow>
      ) : undefined,
    [activeTab, availableTabs, setTab],
  );

  return (
    <AppLayout route="/workspace/school">
      <SchoolOpsHeader tabs={headerTabs} />

      <SchoolHubContentStack>
        {!isProfileLoading && activeTab === "overview" && visibleSections.includes("schools") ? (
          <SchoolsSection embedded hideVaultHelper portalAccess={effectivePortalAccess} />
        ) : null}

        {!isProfileLoading && activeTab === "staff" && visibleSections.includes("school_roles") ? (
          <SchoolRolesSection embedded portalAccess={effectivePortalAccess} />
        ) : null}

        {!isProfileLoading &&
        activeTab === "programmes" &&
        visibleSections.includes("school_sports") ? (
          <SchoolSportsSection embedded portalAccess={effectivePortalAccess} />
        ) : null}

        {!isProfileLoading && activeTab === "media" && visibleSections.includes("schools") ? (
          <SchoolGalleryPanel portalAccess={effectivePortalAccess} selectedSchoolId={schoolId} />
        ) : null}

        {!isProfileLoading && activeTab === "documents" && visibleSections.includes("schools") ? (
          <SchoolDocumentsPanel portalAccess={effectivePortalAccess} selectedSchoolId={schoolId} />
        ) : null}
      </SchoolHubContentStack>
    </AppLayout>
  );
};

export default SchoolHubPage;
